import { Play } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface VideoPlayerProps {
    videoUrl?: string;
    vimeoUrl?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, vimeoUrl }) => {
    const { t } = useTranslation();

    const getIframeSrc = (value?: string) => {
        if (!value) {
            return null;
        }

        const trimmedValue = value.trim();
        const srcMatch = trimmedValue.match(/src=(?:"([^"]+)"|'([^']+)')/i);
        const rawSrc = srcMatch?.[1] || srcMatch?.[2] || trimmedValue;

        if (!rawSrc.startsWith('http://') && !rawSrc.startsWith('https://')) {
            return null;
        }

        // iOS HLS requirement for Bunny embeds
        return rawSrc.replace(/autoplay=true/gi, 'autoplay=false');
    };

    // Bunny video
    const bunnySrc = getIframeSrc(videoUrl);
    if (bunnySrc) {
        return (
            <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                    src={bunnySrc}
                    loading="lazy"
                    style={{
                        border: 0,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: '100%',
                    }}
                    allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                    allowFullScreen
                />
            </div>
        );
    }

    // Vimeo video
    if (vimeoUrl) {
        const vimeoId = vimeoUrl.split('/').pop()?.split('?')[0];
        if (vimeoId) {
            return (
                <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                    <iframe
                        src={`https://player.vimeo.com/video/${vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479`}
                        loading="lazy"
                        style={{
                            border: 0,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: '100%',
                        }}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            );
        }
    }

    // No video
    return (
        <div className="relative aspect-video w-full bg-black">
            <div className="flex h-full items-center justify-center text-center text-gray-400">
                <div>
                    <Play size={64} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">{t('course.noVideoAvailable')}</p>
                </div>
            </div>
        </div>
    );
};

export default VideoPlayer;
